import { Space, Tag as TagAntd, Spin } from 'antd';
import Masonry from 'react-masonry-css';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { Tag, fetchAllTags } from 'src/slices/tag';
import { postsSelector, tagsSelector } from 'src/store';

import { SearchOutlined } from '@ant-design/icons';
import { createUseStyles } from 'react-jss';
import AddArticle from './AddArticle';
import moment from 'moment';
import { DebounceInput } from 'react-debounce-input';
import {
  fetchPostsBySearch,
  fetchPostsByTagsId,
  Post,
  removeAllPosts,
} from 'src/slices/post';
import { User } from '@sentry/types';
import {
  fetchCurrentTeamSettings,
  findUsersByIds,
  Team,
} from 'src/slices/auth';
import { ArticleCard } from 'src/components/ArticleCard';
import { useTeamsFx } from 'src/utils/useTeamsFx';

const { CheckableTag } = TagAntd;

const useStyles = createUseStyles({
  libraryLayout: {
    margin: '50px',
  },
  searchBar: {
    margin: '0 10vw',
  },
  debouncedInput: {
    marginRight: '5px',
    width: '100%',
    border: '1px solid #fff',
    borderRadius: '2px',
    color: 'rgba(0, 0, 0, 0.85)',
    padding: '5px',
  },
  checkableTag: {
    padding: '2px 5px',
    marginRight: '2px',
    fontSize: '14px',
  },
  greyTag: {
    backgroundColor: '#e1edf2',
    color: '#8cc8ff',
    padding: '2px 5px',
    marginRight: '2px',
    fontSize: '14px',
  },
  matchingPostsContent: {
    marginRight: '10px',
    marginLeft: '10px',
  },
  tagsList: {
    margin: '30px',
  },
  myMasonryGrid: {
    display: 'flex',
    width: 'auto',
  },
  myMasonryGridColumn: {
    backgroundClip: 'padding-box',
  },
});

const Library: React.VFC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();

  const { context } = useTeamsFx();

  const tagsStore: Tag[] = useAppSelector(tagsSelector.selectAll);

  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

  const posts: Post[] = useAppSelector(postsSelector.selectAll);
  const [matchingPosts, setMatchingPosts] = useState<Post[]>([]);

  const loadingTags = useAppSelector((state) => state.tag.isLoading);
  const [loadingArticles, setLoadingArticles] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');

  const {
    users,
    team,
  }: { users: { [userId: string]: User }; team: Team | null } = useAppSelector(
    ({ auth: { users, team } }) => ({ users, team }),
  );
  const [teamLoaded, setTeamLoaded] = useState<boolean>(false);

  useEffect(() => {
    moment.locale(i18n.language);
    return () => {
      dispatch(removeAllPosts());
    };
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      if (context?.teamId) {
        try {
          dispatch(fetchAllTags(context.teamId));
          dispatch(fetchCurrentTeamSettings(context?.teamId));
        } catch (error) {}
      }
    };

    fetchSettings();
  }, [context?.teamId]);

  useEffect(() => {
    if (team) {
      setTeamLoaded(true);
    }
  }, [team]);

  useEffect(() => {
    setMatchingPosts(posts);
    setLoadingArticles(false);
    const findUsersAndDisplay = async () => {
      const userIds = posts.map((post) => post.authorId as string);
      await dispatch(
        findUsersByIds(userIds.filter((n, i) => userIds.indexOf(n) === i)),
      );
    };

    findUsersAndDisplay();
  }, [posts]);

  useEffect(() => {
    dispatch(fetchPostsByTagsId(tagsStore.map((tag) => tag._id)));
    setTeamLoaded(true);
  }, [tagsStore]);

  const fetchPosts = async (
    searchString: string,
    offset?: number,
    limit?: number,
  ) => {
    await dispatch(
      fetchPostsBySearch({
        microsoftTeamId: context?.teamId || '',
        paramsQuery: {
          searchPost: searchString,
          offset: offset,
          limit: limit,
        },
      }),
    );
    setLoadingArticles(false);
  };

  const updateSearch = (searchString: string) => {
    if (searchString === '') {
      dispatch(fetchPostsByTagsId(tagsStore.map((tag) => tag._id)));
      setSelectedTags([]);
      return;
    }

    searchString = searchString.trim();

    setSearch(searchString);
    setLoadingArticles(true);
    if (searchString.startsWith('#')) {
      const tagSearch = searchString.slice(1).toLowerCase();

      const filterTags = (tag: Tag) => {
        return tag.name.toLocaleLowerCase().includes(tagSearch);
      };

      const tagsToAdd = tagsStore.filter(filterTags);

      if (tagsToAdd) {
        setSelectedTags(tagsToAdd);
        const fetchSelectedTags = async () => {
          await dispatch(fetchPostsByTagsId(tagsToAdd.map((tag) => tag._id)));
          setLoadingArticles(false);
        };
        fetchSelectedTags();
      } else {
        setLoadingArticles(false);
      }
    } else {
      setSelectedTags([]);
      fetchPosts(searchString);
    }
  };

  const checkTag = (tag: Tag, checked: boolean) => {
    const newSelectedTags = checked
      ? [...selectedTags, tag]
      : selectedTags.filter((selectedTag) => selectedTag._id !== tag._id);

    setSelectedTags(newSelectedTags);

    if (newSelectedTags.length) {
      dispatch(fetchPostsByTagsId(newSelectedTags.map((tag) => tag._id)));
    } else {
      dispatch(fetchPostsByTagsId(tagsStore.map((tag) => tag._id)));
    }
  };

  return (
    <div className={classes.libraryLayout}>
      {teamLoaded && <AddArticle teamId={context?.teamId} />}
      <div className={classes.searchBar}>
        <DebounceInput
          className={classes.debouncedInput}
          value={search}
          onChange={(e) => updateSearch(e.target.value)}
          forceNotifyByEnter={true}
          debounceTimeout={500}
          placeholder={t('common.tagsInput')}
          prefix={<SearchOutlined />}
        />
      </div>
      <Spin tip={t('common.loading')} spinning={loadingArticles && loadingTags}>
        {!loadingTags && (
          <Space size={[2, 10]} wrap className={classes.tagsList}>
            {tagsStore.map((tag) => {
              const isChecked =
                selectedTags.find((tagToFind) => tagToFind._id === tag._id) !==
                undefined;

              return (
                <CheckableTag
                  key={tag._id}
                  checked={isChecked}
                  onChange={(checked) => checkTag(tag, checked)}
                  className={`${isChecked ? '' : 'ant-tag-processing'} ${
                    !isChecked && selectedTags.length
                      ? classes.greyTag
                      : classes.checkableTag
                  }`}
                >
                  {`#${tag.name} (${tag.posts.length})`}
                </CheckableTag>
              );
            })}
          </Space>
        )}

        {matchingPosts.length ? (
          <Masonry
            breakpointCols={3}
            className={classes.myMasonryGrid}
            columnClassName={classes.myMasonryGridColumn}
          >
            {matchingPosts.map((post) => (
              <div key={post._id} className={classes.matchingPostsContent}>
                <ArticleCard
                  post={post}
                  tags={tagsStore}
                  users={users}
                  loadingArticles={loadingArticles}
                  setLoadingArticles={setLoadingArticles}
                />
              </div>
            ))}
          </Masonry>
        ) : null}
      </Spin>
    </div>
  );
};

export default Library;
